<script setup lang="ts">
import type {HsvaColor} from "@uiw/color-convert";
import type {HTMLAttributes} from "vue";
import {hexToHsva, hsvaToHex} from "@uiw/color-convert";
import {ref, watch} from "vue";
import {cn} from "@/lib/utils";
import {Input} from "@/core/components/ui/input";

const props = defineProps<{
    class?: HTMLAttributes["class"];
}>();

/**
 * The picked color, as "#RRGGBB" (uppercase, the backend's stored format).
 */
const model = defineModel<string>({required: true});

const HEX_PATTERN = /^#?[0-9A-Fa-f]{6}$/;

const toHex = (color: HsvaColor) => hsvaToHex(color).toUpperCase();

const clamp = (value: number) => Math.min(1, Math.max(0, value));

/**
 * The picker works in HSV, held apart from the model: a hex string loses the
 * hue of a gray or of black, which would send the hue bar back to red.
 */
const hsva = ref<HsvaColor>(hexToHsva(model.value));

/**
 * What the hex field shows, kept apart so that a half-typed value is not
 * overwritten while the user types it.
 */
const hexDraft = ref(model.value);

watch(model, (hex) => {
    if (hex.toUpperCase() !== toHex(hsva.value)) {
        hsva.value = hexToHsva(hex);
    }

    hexDraft.value = hex;
});

const commit = (color: HsvaColor) => {
    hsva.value = color;
    model.value = toHex(color);
};

/**
 * Follow the pointer from press to release over an area, reporting its
 * position as fractions of the area's width and height.
 */
const track = (event: PointerEvent, onMove: (x: number, y: number) => void) => {
    const area = event.currentTarget as HTMLElement;

    const report = (e: PointerEvent) => {
        const rect = area.getBoundingClientRect();
        onMove(clamp((e.clientX - rect.left) / rect.width), clamp((e.clientY - rect.top) / rect.height));
    };

    const stop = () => {
        area.removeEventListener('pointermove', report);
        area.removeEventListener('pointerup', stop);
        area.removeEventListener('pointercancel', stop);
    };

    area.setPointerCapture(event.pointerId);
    area.addEventListener('pointermove', report);
    area.addEventListener('pointerup', stop);
    area.addEventListener('pointercancel', stop);
    report(event);
};

const pickSaturation = (event: PointerEvent) => {
    track(event, (x, y) => commit({...hsva.value, s: x * 100, v: (1 - y) * 100}));
};

/**
 * The hue stops short of 360, which is red again: the thumb would otherwise
 * jump to the left end on the next model update.
 */
const pickHue = (event: PointerEvent) => {
    track(event, (x) => commit({...hsva.value, h: Math.min(x * 360, 359)}));
};

const typeHex = (value: string | number) => {
    const hex = String(value).trim();
    hexDraft.value = hex;

    if (HEX_PATTERN.test(hex)) {
        commit(hexToHsva(hex.startsWith('#') ? hex : `#${hex}`));
    }
};

/**
 * An incomplete value is dropped on leaving the field.
 */
const resetHexDraft = () => {
    hexDraft.value = model.value;
};
</script>

<template>
  <div :class="cn('flex flex-col gap-3', props.class)">
    <div
      class="relative aspect-[2/1] w-full cursor-crosshair touch-none rounded-lg"
      :style="{
        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsva.h}, 100%, 50%))`
      }"
      @pointerdown="pickSaturation"
    >
      <div
        class="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-solid border-white shadow"
        :style="{left: `${hsva.s}%`, top: `${100 - hsva.v}%`}"
      />
    </div>
    <div
      class="relative h-3 w-full cursor-pointer touch-none rounded-full"
      :style="{
        background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)'
      }"
      @pointerdown="pickHue"
    >
      <div
        class="pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-solid border-white shadow"
        :style="{left: `${(hsva.h / 360) * 100}%`, backgroundColor: `hsl(${hsva.h}, 100%, 50%)`}"
      />
    </div>
    <div class="flex items-center gap-2">
      <div
        class="size-9 shrink-0 rounded-lg border border-solid border-slate-200"
        :style="{backgroundColor: model}"
      />
      <Input
        :model-value="hexDraft"
        class="uppercase"
        maxlength="7"
        placeholder="#3513E2"
        aria-label="Code hexadécimal de la couleur"
        @update:model-value="typeHex"
        @blur="resetHexDraft"
      />
    </div>
  </div>
</template>
